/**
 * StudiesManagement - Standalone Studies Management Component
 *
 * Manages studies with CRUD operations using DataCard for display.
 * Uses StudiesModalV3 which supports time-windowed clinic and device associations.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner, Badge } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import StudiesModalV3 from './modals/StudiesModalV3';
import EntityCSVUploadButton from './EntityCSVUploadButton';
import type { Study, Clinic, Device } from '@/types/associations';

export default function StudiesManagement() {
  // Entity state
  const [studies, setStudies] = useState<Study[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Modal state
  const [modalState, setModalState] = useState<{ show: boolean; editing: Study | null }>({
    show: false,
    editing: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [studiesRes, clinicsRes, devicesRes] = await Promise.all([
        apiClient.getAdminStudies(),
        apiClient.getAdminClinics(),
        apiClient.getAdminDevices(),
      ]);
      if (!studiesRes.error && studiesRes.data) {
        setStudies(studiesRes.data as Study[]);
      }
      if (!clinicsRes.error && clinicsRes.data) {
        setClinics(clinicsRes.data as Clinic[]);
      }
      if (!devicesRes.error && devicesRes.data) {
        setDevices(devicesRes.data as Device[]);
      }
    } catch (error) {
      toast.error('Failed to load studies');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD handlers
  const handleSave = async (data: Partial<Study>) => {
    if (modalState.editing) {
      const response = await apiClient.updateAdminStudy(modalState.editing.id, data);
      if (response.error) throw new Error(response.error);
      toast.success('Study updated successfully');
    } else {
      if (!data.study_id || !data.name) {
        throw new Error('Study ID and name are required');
      }
      const studyPayload: {
        study_id: string;
        name: string;
        description?: string;
        is_active?: boolean;
      } = {
        study_id: String(data.study_id).trim(),
        name: String(data.name).trim(),
      };
      if (data.description && data.description.trim()) {
        studyPayload.description = data.description.trim();
      }
      const dataWithStatus = data as Partial<Study> & { is_active?: boolean };
      if (dataWithStatus.is_active !== undefined) {
        studyPayload.is_active = dataWithStatus.is_active;
      }
      const response = await apiClient.createAdminStudy(studyPayload);
      if (response.error) throw new Error(response.error);
      toast.success('Study created successfully');
    }
    await loadData();
  };

  const handleDelete = (id: number) => {
    const study = studies.find((s) => s.id === id);
    setConfirmModal({
      show: true,
      title: 'Delete Study',
      message: `Are you sure you want to delete "${study?.name || study?.study_id}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteAdminStudy(id);
          if (response.error) throw new Error(response.error);
          toast.success('Study deleted successfully');
          setSelectedId(null);
          await loadData();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete study');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Table rendering
  const renderTable = (items: Study[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Study ID</th>
          <th>Description</th>
          <th>Clinics</th>
          <th>Devices</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((study) => (
          <tr
            key={study.id}
            onClick={() => setSelectedId(selectedId === study.id ? null : study.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedId === study.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><strong>{study.name || study.study_id}</strong></td>
            <td className="text-muted small">{study.study_id}</td>
            <td className="text-muted small" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {study.description || '-'}
            </td>
            <td><Badge bg="info">{study.clinics?.length || 0}</Badge></td>
            <td><Badge bg="secondary">{study.devices?.length || 0}</Badge></td>
            <td>
              <span className={`badge ${study.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                {study.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading studies...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Studies Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage studies and their clinic/device associations
          </p>
        </div>
      </div>

      <DataCard
        cardKey="studies"
        title="Studies"
        badgeCount={studies.length}
        data={studies}
        itemsPerPage={10}
        searchPlaceholder="Search studies..."
        filterFunction={(item, searchTerm) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          return (
            item.name?.toLowerCase().includes(term) ||
            item.study_id.toLowerCase().includes(term) ||
            (item.description || '').toLowerCase().includes(term)
          );
        }}
        headerActions={
          <>
            {selectedId !== null && (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    const study = studies.find((s) => s.id === selectedId);
                    if (study) setModalState({ show: true, editing: study });
                  }}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    if (selectedId !== null) handleDelete(selectedId);
                  }}
                  className="me-2"
                >
                  Delete
                </Button>
              </>
            )}
            <EntityCSVUploadButton
              entityType="studies"
              onUploadComplete={loadData}
              size="sm"
              variant="outline-success"
              className="me-2"
            />
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalState({ show: true, editing: null })}
            >
              Create
            </Button>
          </>
        }
        renderTable={renderTable}
        onRefresh={loadData}
        maxHeight="600px"
        emptyMessage="No studies found. Create your first study."
      />

      {/* Study Modal */}
      <StudiesModalV3
        show={modalState.show}
        study={modalState.editing}
        clinics={clinics}
        devices={devices}
        onHide={() => setModalState({ show: false, editing: null })}
        onSave={handleSave}
        onRefresh={loadData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
